import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Fades its content up once, the first time it scrolls into view. The motion
 * lives in the `.reveal` CSS and only runs when the visitor has not asked for
 * reduced motion; otherwise the content is simply there.
 */
export default function Reveal({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`reveal ${shown ? 'is-shown' : ''} ${className}`}>
      {children}
    </div>
  );
}
